<script setup lang="ts" generic="T">
import type {
  Column,
  ColumnDef,
  ColumnFiltersState,
  ExpandedState,
  SortingState,
  VisibilityState,
} from '@tanstack/vue-table'
import {
  FlexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useVueTable,
} from '@tanstack/vue-table'
import { valueUpdater } from '../../../lib/utils'
import { Button } from '@/components/ui/button'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Icon } from '#components'

const emit = defineEmits<{
  sortingChange: [state: SortingState]
}>()

const { columns: initialColumns, data, defaultSorting, manualSorting } = defineProps<{
  data: T[]
  columns: ColumnDef<T>[]
  defaultSorting?: SortingState
  showSelectedRowCount?: boolean
  manualSorting?: boolean
}>()

const sorting = ref<SortingState>(defaultSorting ?? [])
const columnFilters = ref<ColumnFiltersState>([])
const columnVisibility = ref<VisibilityState>({})
const rowSelection = ref({})
const expanded = ref<ExpandedState>({})

watch(sorting, (newSorting) => {
  emit('sortingChange', newSorting)
})

watch(() => data, () => {
  rowSelection.value = {}
}, { deep: true })

const columns = computed(() => {
  return initialColumns.map(originalColumn => ({
    ...originalColumn,
    originalHeader: originalColumn.header,
    header: originalColumn.enableSorting
      ? ({ column }: { column: Column<T> }) => {
          const originalHeader = originalColumn.header
          return h('button', {
            class: 'cursor-pointer flex items-center gap-2',
            onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
          }, [originalHeader?.toString() ?? '', h(Icon, { name: column.getIsSorted() === 'asc' ? 'tabler-arrow-up' : column.getIsSorted() === 'desc' ? 'tabler-arrow-down' : 'tabler-arrows-sort' })])
        }
      : originalColumn.header,
  }))
})

const table = useVueTable({
  get data() {
    return data
  },

  manualSorting: manualSorting,
  columns: columns.value as ColumnDef<T>[],
  manualPagination: true,
  pageCount: 0,
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getFilteredRowModel: getFilteredRowModel(),
  getExpandedRowModel: getExpandedRowModel(),
  onSortingChange: updaterOrValue => valueUpdater(updaterOrValue, sorting),
  onColumnFiltersChange: updaterOrValue => valueUpdater(updaterOrValue, columnFilters),
  onColumnVisibilityChange: updaterOrValue => valueUpdater(updaterOrValue, columnVisibility),
  onRowSelectionChange: updaterOrValue => valueUpdater(updaterOrValue, rowSelection),
  onExpandedChange: updaterOrValue => valueUpdater(updaterOrValue, expanded),
  state: {
    get sorting() { return sorting.value },
    get columnFilters() { return columnFilters.value },
    get columnVisibility() { return columnVisibility.value },
    get rowSelection() { return rowSelection.value },
    get expanded() { return expanded.value },
  },
})
</script>

<template>
  <div class="w-full">
    <div class="flex flex-row justify-between">
      <div
        v-if="$slots.bulkActions"
        class="flex gap-2 items-center py-4"
        :class="{ 'pointer-events-none opacity-50': table.getFilteredSelectedRowModel().rows.length === 0 }"
      >
        <slot
          name="bulkActions"
          :selected-rows="table.getFilteredSelectedRowModel().rows"
        />
      </div>
      <div v-else-if="$slots.filters">
        <div />
      </div>
      <div
        v-if="$slots.filters"
        class="flex gap-2 items-center py-4"
      >
        <slot name="filters" />
      </div>
    </div>
    <div class="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow
            v-for="headerGroup in table.getHeaderGroups()"
            :key="headerGroup.id"
          >
            <TableHead
              v-for="header in headerGroup.headers"
              :key="header.id"
            >
              <FlexRender
                v-if="!header.isPlaceholder"
                :render="header.column.columnDef.header"
                :props="header.getContext()"
              />
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <template v-if="table.getRowModel().rows?.length">
            <template
              v-for="row in table.getRowModel().rows"
              :key="row.id"
            >
              <TableRow :data-state="row.getIsSelected() && 'selected'">
                <TableCell
                  v-for="cell in row.getVisibleCells()"
                  :key="cell.id"
                >
                  <slot
                    :name="`column-${cell.column.id}`"
                    :row="row"
                  >
                    <FlexRender
                      :render="cell.column.columnDef.cell"
                      :props="cell.getContext()"
                    />
                  </slot>
                </TableCell>
              </TableRow>
              <TableRow v-if="row.getIsExpanded()">
                <TableCell :colspan="row.getAllCells().length">
                  {{ JSON.stringify(row.original) }}
                </TableCell>
              </TableRow>
            </template>
          </template>

          <TableRow v-else>
            <TableCell
              :colspan="columns.length"
              class="h-24 text-center"
            >
              <slot name="empty">
                {{ $t('No results.') }}
              </slot>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>

    <div class="flex items-center justify-end space-x-2 py-4">
      <div
        v-if="showSelectedRowCount"
        class="flex-1 text-sm text-muted-foreground"
      >
        {{ table.getFilteredSelectedRowModel().rows.length }} of
        {{ table.getFilteredRowModel().rows.length }} row(s) selected.
      </div>

      <div class="space-x-2">
        <slot name="pagination">
          <template v-if="table.getCanPreviousPage() || table.getCanNextPage()">
            <Button
              :outlined="true"
              size="sm"
              :disabled="!table.getCanPreviousPage()"
              @click="table.previousPage()"
            >
              {{ $t('Previous') }}
            </Button>
            <Button
              :outlined="true"
              size="sm"
              :disabled="!table.getCanNextPage()"
              @click="table.nextPage()"
            >
              {{ $t('Next') }}
            </Button>
          </template>
        </slot>
      </div>
    </div>
  </div>
</template>
